/**
 * The generic client side of a batch route: send a list of items as a queue
 * of requests, one after another, never more than `chunkSize` per request,
 * and map one result back to each input item (in INPUT order).
 *
 *   while (queue.length) {
 *     chunk = queue.splice(0, chunkSize); res = await send(chunk);
 *     queue.unshift(...chunk.filter(notAttempted));
 *   }
 *
 * The server runs the same single-row operation per item, sequentially, and
 * answers one result per item; a failed row never aborts the rest. An item the
 * server reports as not attempted (its time budget ran out) goes back to the
 * FRONT of the queue, so snapshot order is kept.
 *
 * `outcomeUnknown` is set when a request failed after it may have reached the
 * server — some of its rows may have been processed. That covers a thrown
 * request, a 200 whose results cannot be mapped back to rows, and a response
 * that attempted ZERO items (the route always attempts item 0, so that is a
 * broken server — re-sending would loop forever: the termination guard).
 * Every item not yet answered then gets `failRemaining(item, error)`, and
 * `onRequestFailed` is called ONCE for the run (the caller's single Sentry
 * capture).
 *
 * Callers: `useClearingBatch` (bank feed), the expenses bulk delete. A plain
 * async function, not a mutation: per-row failures resolve, never reject. No
 * toasts and no cache invalidation — the caller aggregates both (see
 * `toastDistinctErrors`).
 */
import { toast } from '@/hooks/useToast';

export interface RunBatchQueueOptions<I, Res, R> {
  items: ReadonlyArray<I>;
  /** Max items per request (the route's own limit). */
  chunkSize: number;
  /** One request carrying `chunk`. */
  send: (chunk: I[]) => Promise<Res>;
  /**
   * The per-item results of a response, or `undefined` when the response is
   * malformed. Must be one per sent item, in sent order.
   */
  resultsOf: (response: Res) => R[] | undefined;
  /** The route's "time budget spent, not tried" result — re-queued. */
  isNotAttempted: (result: R) => boolean;
  /** The result stamped on an item left unanswered by a failed request. */
  failRemaining: (item: I, error: Error) => R;
  /** Called once when a request fails (capture it once, here). */
  onRequestFailed?: (error: Error, info: { sentCount: number; total: number }) => void;
  /** Called after each response with the items that have a final result so far. */
  onProgress?: (done: number, total: number) => void;
  /** Prefix for the synthetic error messages (default `'Batch'`). */
  label?: string;
}

export interface BatchQueueRun<R> {
  /** One result per input item, in INPUT order. */
  results: R[];
  /** A request failed after it may have reached the server. */
  outcomeUnknown: boolean;
}

export async function runBatchQueue<I, Res, R>({
  items,
  chunkSize,
  send,
  resultsOf,
  isNotAttempted,
  failRemaining,
  onRequestFailed,
  onProgress,
  label = 'Batch',
}: RunBatchQueueOptions<I, Res, R>): Promise<BatchQueueRun<R>> {
  const total = items.length;
  const results: Array<R | undefined> = new Array(total);
  let outcomeUnknown = false;
  if (total === 0) return { results: [], outcomeUnknown };

  const fail = (error: Error, sentCount: number) => {
    onRequestFailed?.(error, { sentCount, total });
    for (let i = 0; i < total; i++) {
      if (results[i] === undefined) results[i] = failRemaining(items[i], error);
    }
  };

  // Input indices, so a re-queued item still lands in its own result slot.
  const queue = items.map((_, i) => i);
  let done = 0;

  while (queue.length) {
    const chunk = queue.splice(0, Math.max(1, chunkSize));
    let response: Res;
    try {
      response = await send(chunk.map((i) => items[i]));
    } catch (e) {
      // The request may have reached the server and run some rows before it
      // failed (proxy timeout, dropped connection) — nobody knows which.
      outcomeUnknown = true;
      fail(e instanceof Error ? e : new Error(String(e)), chunk.length);
      break;
    }

    const chunkResults = resultsOf(response);
    if (!Array.isArray(chunkResults) || chunkResults.length !== chunk.length) {
      // A 200 we cannot map back to rows: the server did answer, so rows may
      // have been processed.
      outcomeUnknown = true;
      fail(new Error(`${label} returned a malformed response`), chunk.length);
      break;
    }

    const retry: number[] = [];
    chunkResults.forEach((r, k) => {
      if (isNotAttempted(r)) retry.push(chunk[k]);
      else results[chunk[k]] = r;
    });

    if (retry.length === chunk.length) {
      // Termination guard: nothing attempted, so re-sending would loop. An
      // earlier chunk may have run; the caller refreshes on outcomeUnknown.
      outcomeUnknown = true;
      fail(new Error(`${label} attempted no operations`), chunk.length);
      break;
    }

    queue.unshift(...retry);
    done += chunk.length - retry.length;
    onProgress?.(done, total);
  }

  return { results: results as R[], outcomeUnknown };
}

/**
 * One `toast.error` per DISTINCT failure message of a bulk run — each row's
 * own server message, without N identical toasts for N rows refused the same
 * way.
 */
export function toastDistinctErrors(messages: Iterable<string>) {
  for (const message of new Set(messages)) toast.error(message);
}
